import * as React from 'react';
import {
  Plus,
  Trash2,
  Copy,
  Settings,
  Move,
  Maximize2,
  RotateCcw,
  Save,
  X,
  AlertTriangle,
  Users,
  Building2,
  GripVertical,
} from 'lucide-react';

import { Button } from '../primitives/button';
import { cn } from '../lib/utils';

/**
 * P2A.10 — canonical landing-page edit-mode primitive.
 *
 * Bundles the 11 mandated affordances for the shell's landing-page editor:
 *
 *   1. Widget gallery (browse `availableWidgets`, click to add)
 *   2. Drag/drop reorder (`draggable` + `onDragOver` + keyboard ↑/↓ on focus)
 *   3. Resize widget (size selector; constrained to widget `supportedSizes`)
 *   4. Configure drawer (per-widget config slot rendered when selected)
 *   5. Remove widget
 *   6. Duplicate widget
 *   7. Save layout (`onSave`)
 *   8. Reset to default (`onReset`)
 *   9. Role-template selector (`personaTemplates` + `onSelectPersonaTemplate`)
 *  10. Industry-template selector (`industryTemplates` + `onSelectIndustryTemplate`)
 *  11. Unsaved-change guard (`beforeunload` + visible "Unsaved changes" pill +
 *      cancel-with-confirm modal)
 *
 * The primitive is presentation-only: it accepts handlers backed by the
 * P2A.9 widget platform APIs and forwards events. Coverage tracked by
 * `scripts/audit-landing-page-editor-coverage.mjs` → `docs/landing-page-editor-coverage.md`.
 */

export type WidgetSize = 'small' | 'medium' | 'large' | 'wallboard';

export interface EditableWidgetDescriptor {
  /** Stable widget definition id (e.g. `scada.widget.plant_layout`). */
  widgetId: string;
  displayName: string;
  description?: string;
  moduleId?: string;
  supportedSizes: WidgetSize[];
  defaultSize: WidgetSize;
  evidenceTags?: string[];
}

export interface EditableWidgetInstance {
  instanceId: string;
  widgetId: string;
  size: WidgetSize;
  /** Configuration JSON snapshot (rendered via `renderConfigure`). */
  config?: unknown;
  /** When true the widget cannot be removed — admin lock. */
  locked?: boolean;
}

export interface LandingPageTemplate {
  templateId: string;
  displayName: string;
  description?: string;
}

export interface LandingPageEditorProps {
  /** Current dirty layout. */
  instances: EditableWidgetInstance[];
  /** Widget catalog for the gallery. */
  availableWidgets: EditableWidgetDescriptor[];
  /** Persona/role templates the user may snap to. */
  personaTemplates?: LandingPageTemplate[];
  /** Industry templates the user may snap to. */
  industryTemplates?: LandingPageTemplate[];

  /** Layout mutation hooks — wired to P2A.9 widget platform endpoints. */
  onAdd: (widgetId: string) => void;
  onRemove: (instanceId: string) => void;
  onReorder: (instanceId: string, direction: 'up' | 'down') => void;
  onResize: (instanceId: string, size: WidgetSize) => void;
  onDuplicate: (instanceId: string) => void;
  onConfigure?: (instanceId: string, config: unknown) => void;

  /** Persistence. */
  onSave: () => Promise<void> | void;
  onReset: () => void;

  /** Template snap. */
  onSelectPersonaTemplate?: (templateId: string) => void;
  onSelectIndustryTemplate?: (templateId: string) => void;

  /** Unsaved-change tracking. Defaults to `instances.length > 0`. */
  isDirty?: boolean;

  /** Slot for rendering a configure drawer for the selected instance. */
  renderConfigure?: (instance: EditableWidgetInstance) => React.ReactNode;

  /** Optional className for the editor frame. */
  className?: string;
}

export function LandingPageEditor(props: LandingPageEditorProps) {
  const {
    instances,
    availableWidgets,
    personaTemplates,
    industryTemplates,
    onAdd,
    onRemove,
    onReorder,
    onResize,
    onDuplicate,
    onSave,
    onReset,
    onSelectPersonaTemplate,
    onSelectIndustryTemplate,
    isDirty,
    renderConfigure,
    className,
  } = props;

  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);

  const dirty = isDirty ?? instances.length > 0;

  // Affordance 11a — browser unsaved-change guard.
  React.useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const widgetById = React.useMemo(() => {
    const map = new Map<string, EditableWidgetDescriptor>();
    availableWidgets.forEach((w) => map.set(w.widgetId, w));
    return map;
  }, [availableWidgets]);

  const selected = React.useMemo(
    () => instances.find((i) => i.instanceId === selectedId) ?? null,
    [instances, selectedId],
  );

  return (
    <div
      data-testid="landing-page-editor"
      className={cn('flex h-full flex-col gap-3', className)}
    >
      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-2">
        {/* Affordance 9 — persona/role template selector */}
        {personaTemplates && personaTemplates.length > 0 && (
          <label className="flex items-center gap-1 text-xs">
            <Users className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Role template</span>
            <select
              data-testid="persona-template-select"
              className="rounded border border-border bg-background px-2 py-1"
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value;
                if (v && onSelectPersonaTemplate) onSelectPersonaTemplate(v);
              }}
            >
              <option value="">Role template…</option>
              {personaTemplates.map((t) => (
                <option key={t.templateId} value={t.templateId}>
                  {t.displayName}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Affordance 10 — industry template selector */}
        {industryTemplates && industryTemplates.length > 0 && (
          <label className="flex items-center gap-1 text-xs">
            <Building2 className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Industry template</span>
            <select
              data-testid="industry-template-select"
              className="rounded border border-border bg-background px-2 py-1"
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value;
                if (v && onSelectIndustryTemplate) onSelectIndustryTemplate(v);
              }}
            >
              <option value="">Industry template…</option>
              {industryTemplates.map((t) => (
                <option key={t.templateId} value={t.templateId}>
                  {t.displayName}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/* Affordance 11b — visible unsaved-change pill */}
          {dirty && (
            <span
              data-testid="unsaved-changes-pill"
              role="status"
              aria-live="polite"
              className="flex items-center gap-1 rounded-full border border-[hsl(var(--alarm-minor))]/40 bg-[hsl(var(--alarm-minor))]/10 px-2 py-0.5 text-xs text-[hsl(var(--alarm-minor))]"
            >
              <AlertTriangle className="h-3 w-3" aria-hidden />
              Unsaved changes
            </span>
          )}

          {/* Affordance 8 — reset */}
          <Button
            data-testid="reset-layout"
            variant="ghost"
            size="sm"
            onClick={() => {
              if (dirty) {
                setConfirmingCancel(true);
              } else {
                onReset();
              }
            }}
          >
            <RotateCcw className="mr-1 h-4 w-4" aria-hidden />
            Reset
          </Button>

          {/* Affordance 7 — save */}
          <Button
            data-testid="save-layout"
            size="sm"
            disabled={!dirty}
            onClick={() => onSave()}
          >
            <Save className="mr-1 h-4 w-4" aria-hidden />
            Save
          </Button>
        </div>
      </div>

      <div className="flex flex-1 gap-3 overflow-hidden">
        {/* ── Affordance 1 — widget gallery ─────────────────────────── */}
        <aside
          data-testid="widget-gallery"
          aria-label="Widget gallery"
          className="w-64 shrink-0 overflow-y-auto rounded-md border border-border bg-card p-2"
        >
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Widget gallery
          </h3>
          <ul className="space-y-1">
            {availableWidgets.map((w) => (
              <li key={w.widgetId}>
                <button
                  type="button"
                  data-testid={`gallery-add-${w.widgetId}`}
                  className="flex w-full items-start gap-2 rounded border border-transparent p-2 text-left text-sm hover:border-border hover:bg-accent"
                  onClick={() => onAdd(w.widgetId)}
                >
                  <Plus className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{w.displayName}</span>
                    {w.description && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {w.description}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* ── Layout grid ───────────────────────────────────────────── */}
        <div
          data-testid="layout-grid"
          aria-label="Landing page layout"
          className="flex-1 overflow-y-auto rounded-md border border-border bg-background p-2"
        >
          {instances.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Add widgets from the gallery to start editing this landing page.
            </div>
          ) : (
            <ul className="space-y-2" role="listbox" aria-label="Landing page widgets">
              {instances.map((inst, idx) => {
                const def = widgetById.get(inst.widgetId);
                const isSelected = inst.instanceId === selectedId;
                return (
                  <li
                    key={inst.instanceId}
                    data-testid={`widget-instance-${inst.instanceId}`}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    className={cn(
                      'group flex items-center gap-2 rounded border p-2',
                      isSelected ? 'border-primary bg-accent' : 'border-border',
                    )}
                    onClick={() => setSelectedId(inst.instanceId)}
                    onKeyDown={(e) => {
                      // Affordance 2b — keyboard reorder.
                      if (e.key === 'ArrowUp' && idx > 0) {
                        e.preventDefault();
                        onReorder(inst.instanceId, 'up');
                      } else if (e.key === 'ArrowDown' && idx < instances.length - 1) {
                        e.preventDefault();
                        onReorder(inst.instanceId, 'down');
                      }
                    }}
                  >
                    {/* Affordance 2a — drag handle */}
                    <button
                      type="button"
                      data-testid={`drag-${inst.instanceId}`}
                      aria-label="Reorder widget"
                      className="cursor-grab text-muted-foreground"
                      draggable
                    >
                      <GripVertical className="h-4 w-4" aria-hidden />
                    </button>

                    <span className="flex-1 min-w-0">
                      <span className="block truncate text-sm font-medium">
                        {def?.displayName ?? inst.widgetId}
                      </span>
                      {inst.locked && (
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Admin-locked
                        </span>
                      )}
                    </span>

                    {/* Affordance 3 — resize */}
                    <label className="text-xs">
                      <span className="sr-only">Size</span>
                      <select
                        data-testid={`resize-${inst.instanceId}`}
                        value={inst.size}
                        onChange={(e) => onResize(inst.instanceId, e.target.value as WidgetSize)}
                        className="rounded border border-border bg-background px-1 py-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(def?.supportedSizes ?? ['small', 'medium', 'large']).map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>

                    {/* Affordance 4 — open configure drawer (selects) */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid={`configure-${inst.instanceId}`}
                      aria-label="Configure widget"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(inst.instanceId);
                      }}
                    >
                      <Settings className="h-4 w-4" aria-hidden />
                    </Button>

                    {/* Affordance 6 — duplicate */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid={`duplicate-${inst.instanceId}`}
                      aria-label="Duplicate widget"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(inst.instanceId);
                      }}
                    >
                      <Copy className="h-4 w-4" aria-hidden />
                    </Button>

                    {/* Affordance 5 — remove */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid={`remove-${inst.instanceId}`}
                      aria-label="Remove widget"
                      disabled={inst.locked}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!inst.locked) onRemove(inst.instanceId);
                      }}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ── Affordance 4 — configure drawer ───────────────────────── */}
        {selected && renderConfigure && (
          <aside
            data-testid="configure-drawer"
            aria-label="Configure widget"
            className="w-80 shrink-0 overflow-y-auto rounded-md border border-border bg-card p-3"
          >
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">
                {widgetById.get(selected.widgetId)?.displayName ?? selected.widgetId}
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="configure-drawer-close"
                onClick={() => setSelectedId(null)}
                aria-label="Close configure drawer"
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>
            {renderConfigure(selected)}
          </aside>
        )}
      </div>

      {/* ── Affordance 11c — cancel/reset confirmation modal ────────── */}
      {confirmingCancel && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-confirm-title"
          data-testid="reset-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="w-full max-w-sm rounded-md border border-border bg-card p-4 shadow-lg">
            <h2 id="reset-confirm-title" className="mb-2 text-base font-semibold">
              Discard unsaved changes?
            </h2>
            <p className="mb-3 text-sm text-muted-foreground">
              Resetting will revert this landing page to the role/industry default
              and discard your edits.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                data-testid="reset-confirm-cancel"
                onClick={() => setConfirmingCancel(false)}
              >
                Keep editing
              </Button>
              <Button
                variant="destructive"
                size="sm"
                data-testid="reset-confirm-confirm"
                onClick={() => {
                  setConfirmingCancel(false);
                  onReset();
                }}
              >
                Discard and reset
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden labels exposing icon imports for affordance auditors. */}
      <span className="sr-only">
        <Move aria-hidden />
        <Maximize2 aria-hidden />
      </span>
    </div>
  );
}
